import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Event } from '../../types';
import type { UnifiedTransfer } from '../../hooks/useTransfers';
import { formatCurrency } from '../currency';

// RGB tuple type for colors
type RGB = [number, number, number];

// Color palette (RGB arrays for jsPDF)
const COLORS = {
  primary: [76, 175, 147] as RGB, // Mint green
  primaryLight: [232, 245, 241] as RGB, // Light mint background
  primaryDark: [56, 142, 118] as RGB, // Darker mint for contrast
  textPrimary: [31, 41, 55] as RGB, // Gray-800
  textSecondary: [107, 114, 128] as RGB, // Gray-500
  border: [229, 231, 235] as RGB, // Gray-200
  white: [255, 255, 255] as RGB,
  backgroundLight: [249, 250, 251] as RGB, // Gray-50
};

export interface SettlementPDFData {
  event: Event;
  totalSpent: number;
  perPerson: number;
  transfers: UnifiedTransfer[];
}

export function generateSettlementPDF(data: SettlementPDFData): Blob {
  const { event, totalSpent, perPerson, transfers } = data;
  const currency = event.currency;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - 2 * margin;
  let y = margin;

  // ============================================
  // HEADER SECTION - Rounded rectangle with background
  // ============================================
  const headerHeight = 35;

  // Draw rounded rectangle background
  doc.setFillColor(...COLORS.primaryLight);
  doc.roundedRect(margin, y, contentWidth, headerHeight, 4, 4, 'F');

  // Event name
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.textPrimary);
  doc.text(event.name, pageWidth / 2, y + 15, { align: 'center' });

  // Date
  const today = new Date().toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.textSecondary);
  doc.text(today, pageWidth / 2, y + 25, { align: 'center' });

  y += headerHeight + 12;

  // ============================================
  // SUMMARY CARDS - Three cards in a row
  // ============================================
  const cardWidth = (contentWidth - 8) / 3; // 8mm total gap (4mm between each card)
  const cardHeight = 28;
  const cardGap = 4;

  const summaryData = [
    { label: 'Total', value: formatCurrency(totalSpent, currency) },
    { label: 'Participantes', value: String(event.participants.length) },
    { label: 'Por persona', value: formatCurrency(perPerson, currency) },
  ];

  summaryData.forEach((item, index) => {
    const cardX = margin + index * (cardWidth + cardGap);

    // Card background
    doc.setFillColor(...COLORS.white);
    doc.setDrawColor(...COLORS.border);
    doc.roundedRect(cardX, y, cardWidth, cardHeight, 3, 3, 'FD');

    // Label
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.textSecondary);
    doc.text(item.label, cardX + cardWidth / 2, y + 10, { align: 'center' });

    // Value
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.textPrimary);
    doc.text(item.value, cardX + cardWidth / 2, y + 20, { align: 'center' });
  });

  y += cardHeight + 15;

  // ============================================
  // EXPENSES TABLE
  // ============================================
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.textPrimary);
  doc.text('GASTOS', margin, y);
  y += 6;

  if (event.expenses.length === 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.textSecondary);
    doc.text('No hay gastos registrados', margin, y + 6);
    y += 15;
  } else {
    // Create participant map for quick lookup
    const participantMap = new Map(event.participants.map((p) => [p.id, p.name]));

    // Prepare table data
    const expenseRows = event.expenses.map((expense, index) => [
      String(index + 1),
      expense.description,
      participantMap.get(expense.paidById) || 'Desconocido',
      formatCurrency(expense.amount, currency),
    ]);

    // Add total row
    expenseRows.push(['', '', 'Total', formatCurrency(totalSpent, currency)]);

    autoTable(doc, {
      startY: y,
      head: [['#', 'Descripcion', 'Pago', 'Monto']],
      body: expenseRows,
      theme: 'plain',
      headStyles: {
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontStyle: 'bold',
        fontSize: 10,
        cellPadding: 4,
      },
      bodyStyles: {
        fontSize: 10,
        cellPadding: 3,
        textColor: COLORS.textPrimary,
      },
      alternateRowStyles: {
        fillColor: COLORS.backgroundLight,
      },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 35 },
        3: { cellWidth: 35, halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: margin, right: margin },
      didParseCell: (data) => {
        // Style the total row
        if (data.row.index === expenseRows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          if (data.column.index === 2 || data.column.index === 3) {
            data.cell.styles.fillColor = COLORS.primaryLight;
          }
        }
      },
    });

    // Get the final Y position after the table
    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 12;
  }

  // ============================================
  // TRANSFERS TABLE
  // ============================================

  // Check if we need a new page
  if (y > pageHeight - 60) {
    doc.addPage();
    y = margin;
  }

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.textPrimary);
  doc.text('TRANSFERENCIAS NECESARIAS', margin, y);
  y += 6;

  if (transfers.length === 0) {
    // Draw "all settled" box
    const settledBoxHeight = 25;
    doc.setFillColor(...COLORS.primaryLight);
    doc.roundedRect(margin, y, contentWidth, settledBoxHeight, 3, 3, 'F');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.primary);
    doc.text('Todo saldado', pageWidth / 2, y + 10, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.textSecondary);
    doc.text('No hay transferencias pendientes', pageWidth / 2, y + 18, { align: 'center' });

    y += settledBoxHeight + 10;
  } else {
    // Prepare transfer data
    const transferRows = transfers.map((transfer) => {
      const fromLabel = transfer.fromIsGroup
        ? `${transfer.fromName} (${transfer.fromDisplayName})`
        : transfer.fromName;
      const toLabel = transfer.toIsGroup
        ? `${transfer.toName} (${transfer.toDisplayName})`
        : transfer.toName;

      return [fromLabel, toLabel, formatCurrency(transfer.amount, currency)];
    });

    autoTable(doc, {
      startY: y,
      head: [['De', 'A', 'Monto']],
      body: transferRows,
      theme: 'plain',
      headStyles: {
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontStyle: 'bold',
        fontSize: 10,
        cellPadding: 4,
      },
      bodyStyles: {
        fontSize: 10,
        cellPadding: 4,
        textColor: COLORS.textPrimary,
      },
      alternateRowStyles: {
        fillColor: COLORS.backgroundLight,
      },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: margin, right: margin },
    });

    y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
  }

  // ============================================
  // FOOTER
  // ============================================
  const footerY = pageHeight - 15;

  // Line
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);

  // Footer text
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.textSecondary);
  doc.text('Generado con Dividy', pageWidth / 2, footerY, { align: 'center' });

  return doc.output('blob');
}

export function generateFilename(eventName: string): string {
  const sanitized = eventName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const date = new Date().toISOString().split('T')[0];
  return `dividy-${sanitized}-${date}.pdf`;
}
